import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  type ChangeEvent,
  type FC,
} from "react";
import { Select, Input, InputNumber, Button } from "antd";
import type { TableProps } from "antd";
import type { ColumnsType } from "antd/es/table/interface";
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import "./stylesWallet.css";
import AppContext from "../../../context/AppContext";
import {
  useGetWalletTransactions,
  type WalletBalanceFilter,
  type WalletBalanceType,
  type WalletBalanceOperator,
  type WalletSearchBy,
  type WalletSortOrder,
  type WalletTransactionItem,
  type WalletTransactionsPagination,
} from "../../../queries/transactions/wallet/useGetWalletTransactions";

import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { SIDER_TABS, BRAND_ICONS_URLS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import { resolveServerSorter } from "../../shared/table-filters/filterQueryUtils";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import SaveButton from "../../CommonComponents/SaveButton/SaveButton";
import CustomPagination from "../../shared/CustomPagination/CustomPagination";

type AppContextValue = {
  setActiveSidebarTab: (section: string, label: string) => void;
};

type SelectOption<V extends string> = { label: string; value: V };

type WalletTableOnChange = NonNullable<
  TableProps<WalletTransactionItem>["onChange"]
>;

type SaveButtonProps = {
  isApiRunning: boolean;
  callbackFunction: () => void;
  buttonText: string;
};
const TypedSaveButton = SaveButton as unknown as FC<SaveButtonProps>;

type CustomPaginationProps = {
  total: number;
  pageSize: number;
  currentPage: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
};
const TypedCustomPagination =
  CustomPagination as unknown as FC<CustomPaginationProps>;

type DataTableProps = {
  columns: ColumnsType<WalletTransactionItem>;
  dataSource: WalletTransactionItem[];
  scrollPreset: string;
  pagination: false;
  loading: boolean;
  onChange: WalletTableOnChange;
  rowKey: (record: WalletTransactionItem, index?: number) => string;
};
const TypedDataTable = DataTable as unknown as FC<DataTableProps>;

const BALANCE_TYPE_OPTIONS: SelectOption<WalletBalanceType>[] = [
  {
    label: AppStrings.CASH_BALANCE || "Cash Balance",
    value: "primary_currency_balance",
  },
  {
    label: AppStrings.REDEEMABLE_CASH_BALANCE || "Redeemable Cash Balance",
    value: "primary_not_wagered_currency_balance",
  },
];

const OPERATOR_OPTIONS: SelectOption<WalletBalanceOperator>[] = [
  { label: ">=", value: "gte" },
  { label: "<=", value: "lte" },
  { label: "=", value: "eq" },
  { label: ">", value: "gt" },
  { label: "<", value: "lt" },
];

const SEARCH_BY_OPTIONS: SelectOption<WalletSearchBy>[] = [
  { label: AppStrings.USERNAME, value: "username" },
  { label: AppStrings.USER_ID, value: "user_id" },
  { label: AppStrings.EMAIL, value: "email" },
];

const Wallet: FC = () => {
  usePageTitle(AppStrings.WALLET);

  const ctx = useContext(AppContext) as AppContextValue;
  const { setActiveSidebarTab } = ctx;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchBy, setSearchBy] = useState<WalletSearchBy>("username");

  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [appliedSearchBy, setAppliedSearchBy] =
    useState<WalletSearchBy>("username");

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<WalletSortOrder | null>(null);

  const [balanceFilters, setBalanceFilters] = useState<WalletBalanceFilter[]>(
    [],
  );
  const [appliedBalanceFilters, setAppliedBalanceFilters] = useState<
    WalletBalanceFilter[]
  >([]);

  const [subregion, setSubregion] = useState<string>("");
  const [appliedSubregion, setAppliedSubregion] = useState<string>("");

  const {
    data: walletData,
    isLoading: isWalletLoading,
    isFetching: isWalletFetching,
  } = useGetWalletTransactions({
    page: currentPage,
    page_size: pageSize,
    sort_by: sortBy,
    sort_order: sortOrder,
    search_term: appliedSearchTerm || null,
    search_by: appliedSearchTerm ? appliedSearchBy : null,
    balance_filters: appliedBalanceFilters,
    registration_subregion: appliedSubregion || null,
  });

  const walletTransactions = useMemo<WalletTransactionItem[]>(
    () => walletData?.transactions ?? [],
    [walletData?.transactions],
  );

  const paginationData: WalletTransactionsPagination =
    walletData?.pagination ?? {
      current_page: currentPage,
      page_size: pageSize,
      total_items: walletTransactions.length,
      total_pages: walletTransactions.length
        ? Math.ceil(walletTransactions.length / pageSize)
        : 0,
      has_next: false,
      has_prev: currentPage > 1,
    };

  const handleViewUserClicked = useCallback(
    (user_data: WalletTransactionItem) => {
      const url = `${NAVIGATION_ROUTES?.USER_MANAGER}?userId=${user_data.user_id}`;
      window.open(url, "_blank");
    },
    [],
  );

  const handleTableChange: WalletTableOnChange = useCallback(
    (_pagination, _filters, sorter) => {
      const { sortBy: nextSortBy, sortOrder: nextSortOrder } =
        resolveServerSorter(sorter) as {
          sortBy: string | null;
          sortOrder: WalletSortOrder | null;
        };

      if (nextSortBy && nextSortOrder) {
        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
        setCurrentPage(1);
        return;
      }

      if (sortBy || sortOrder) {
        setSortBy(null);
        setSortOrder(null);
        setCurrentPage(1);
      }
    },
    [sortBy, sortOrder],
  );

  const columns: ColumnsType<WalletTransactionItem> = [
    {
      title: AppStrings.USER_ID,
      dataIndex: "user_id",
      key: "user_id",
      render: (_text: string, record: WalletTransactionItem) => (
        <span
          className="wallet-transactions-table-user-id-text"
          onClick={() => handleViewUserClicked(record)}
        >
          {record.user_id}
        </span>
      ),
    },
    {
      title: AppStrings.NAME,
      dataIndex: "name",
      key: "name",
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      render: (_text: string, record: WalletTransactionItem) => (
        <div onClick={() => handleViewUserClicked(record)}>
          <span className="wallet-transactions-table-username">
            {record.username}
          </span>
        </div>
      ),
    },
    {
      title: AppStrings.EMAIL,
      dataIndex: "email",
      key: "email",
      render: (_text: string, record: WalletTransactionItem) => (
        <div onClick={() => handleViewUserClicked(record)}>
          <span className="wallet-transactions-table-email">
            {record.email}
          </span>
        </div>
      ),
    },
    {
      title: AppStrings.CASH_BALANCE,
      dataIndex: "primary_currency_balance",
      key: "primary_currency_balance",
      sorter: true,
      sortOrder:
        sortBy === "primary_currency_balance"
          ? sortOrder === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (_text: number, record: WalletTransactionItem) => {
        const pct = record.cash_balance_7d_change_pct ?? 0;
        const hasPct = !!pct;
        const pctColor =
          pct > 0
            ? "var(--green-400)"
            : pct < 0
              ? "var(--red-400)"
              : "inherit";
        const pctSign = pct > 0 ? "+" : "";
        return (
          <div className="wallet-transactions-table-currency-container">
            <img
              src={
                process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                BRAND_ICONS_URLS.primaryCurrencySymbol
              }
              alt="cash icon"
              className="indices-currency-icon"
            />
            <span>
              {record.primary_currency_balance?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) ?? "0.00"}
            </span>
            {hasPct && (
              <span
                style={{
                  color: pctColor,
                  fontSize: "11px",
                  marginLeft: "8px",
                }}
              >
                {pctSign}
                {pct.toFixed(1)}%
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: AppStrings.REDEEMABLE_CASH_BALANCE,
      dataIndex: "redeemable_cash_balance",
      key: "redeemable_cash_balance",
      sorter: true,
      sortOrder:
        sortBy === "redeemable_cash_balance"
          ? sortOrder === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (_text: number, record: WalletTransactionItem) => (
        <div className="wallet-transactions-table-currency-container">
          <img
            src={
              process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
              BRAND_ICONS_URLS.primaryCurrencySymbol
            }
            alt="cash icon"
            className="indices-currency-icon"
          />
          <span>
            {record.redeemable_cash_balance?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) ?? "0.00"}
          </span>
        </div>
      ),
    },
    {
      title: AppStrings.STATE,
      dataIndex: "registration_subregion",
      key: "registration_subregion",
      render: (_text: string | null, record: WalletTransactionItem) => {
        const value = record.registration_subregion;
        return <span>{value && value.trim() ? value : "—"}</span>;
      },
    },
  ];

  const handlePageSizeChange = useCallback((value: number) => {
    setPageSize(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedSearchTerm(searchTerm);
    setAppliedSearchBy(searchBy);

    const validFilters = balanceFilters.filter(
      (f) =>
        f.balance_type &&
        f.operator &&
        f.operand !== null &&
        f.operand !== undefined,
    );
    setAppliedBalanceFilters(validFilters);

    setAppliedSubregion(subregion.trim());

    setCurrentPage(1);
  }, [searchTerm, searchBy, balanceFilters, subregion]);

  const handleSearchTermChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleSearchByChange = useCallback((value: WalletSearchBy) => {
    setSearchBy(value);
  }, []);

  const handleAddBalanceFilter = useCallback(() => {
    setBalanceFilters((prev) => [
      ...prev,
      {
        balance_type: "primary_currency_balance",
        operator: "gte",
        operand: 0,
      },
    ]);
  }, []);

  const handleRemoveBalanceFilter = useCallback((index: number) => {
    setBalanceFilters((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleBalanceFilterChange = useCallback(
    <K extends keyof WalletBalanceFilter>(
      index: number,
      field: K,
      value: WalletBalanceFilter[K],
    ) => {
      setBalanceFilters((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setSearchBy("username");
    setAppliedSearchTerm("");
    setAppliedSearchBy("username");
    setBalanceFilters([]);
    setAppliedBalanceFilters([]);
    setSubregion("");
    setAppliedSubregion("");
    setSortBy(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.PAYMENTS, "WALLET");
  }, [setActiveSidebarTab]);

  return (
    <div
      className="wallet-transactions-container"
      data-testid="wallet-transactions-container"
    >
        <div className="wallet-transactions-header">
          <div className="wallet-transactions-header-selects-container">
            <div className="wallet-transactions-header-selects-container-left">
              <div className="wallet-transactions-header-select wallet-transactions-header-actions-search-by-select">
                <span>{AppStrings.SEARCH_BY}</span>
                <Select<WalletSearchBy>
                  value={searchBy}
                  onChange={handleSearchByChange}
                  options={SEARCH_BY_OPTIONS}
                />
              </div>
              <div className="wallet-transactions-header-actions-container-search-bar">
                <Input
                  placeholder={`Search by ${searchBy}...`}
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  onPressEnter={handleApplyFilters}
                  allowClear
                  prefix={<SearchOutlined />}
                />
              </div>
              <div className="wallet-transactions-header-select wallet-transactions-header-actions-subregion-input">
                <Input
                  placeholder={AppStrings.STATE}
                  value={subregion}
                  onChange={(e) => setSubregion(e.target.value)}
                  onPressEnter={handleApplyFilters}
                  allowClear
                />
              </div>
            </div>
            <div className="wallet-transactions-header-action-buttons">
              <Button
                onClick={handleClearFilters}
                className="wallet-transactions-clear-btn"
                style={{ display: "none" }}
              >
                Clear
              </Button>
              <TypedSaveButton
                isApiRunning={isWalletLoading}
                callbackFunction={handleApplyFilters}
                buttonText={AppStrings.APPLY}
              />
            </div>
          </div>

          {/* Balance Filters Section
              hidden untile server-side filtering is ready for
              primary_not_wagered_currency_balance (redeemable_cash_balance))
          */}
          <div
            className="wallet-transactions-balance-filters"
            style={{ display: "none" }}
          >
            <div className="wallet-transactions-balance-filters-header">
              <span className="wallet-transactions-balance-filters-title">
                Balance Filters
              </span>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddBalanceFilter}
                size="small"
                className="wallet-transactions-add-filter-btn"
              >
                Add Filter
              </Button>
            </div>
            {balanceFilters.length > 0 && (
              <div className="wallet-transactions-balance-filters-list">
                {balanceFilters.map((filter, index) => (
                  <div
                    key={index}
                    className="wallet-transactions-balance-filter-row"
                  >
                    <Select<WalletBalanceType>
                      value={filter.balance_type}
                      onChange={(value) =>
                        handleBalanceFilterChange(index, "balance_type", value)
                      }
                      options={BALANCE_TYPE_OPTIONS}
                      style={{ width: 200 }}
                    />
                    <Select<WalletBalanceOperator>
                      value={filter.operator}
                      onChange={(value) =>
                        handleBalanceFilterChange(index, "operator", value)
                      }
                      options={OPERATOR_OPTIONS}
                      style={{ width: 80 }}
                    />
                    <InputNumber
                      value={filter.operand}
                      onChange={(value) =>
                        handleBalanceFilterChange(
                          index,
                          "operand",
                          (value as number) ?? null,
                        )
                      }
                      min={0}
                      style={{ width: 120 }}
                      placeholder="Amount"
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveBalanceFilter(index)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="wallet-transactions-body">
          <div className="wallet-transactions-table-container">
            <TypedDataTable
              columns={columns}
              dataSource={walletTransactions}
              scrollPreset="verticalOnly"
              pagination={false}
              loading={isWalletFetching || isWalletLoading}
              onChange={handleTableChange}
              rowKey={(record, index) => record.user_id || String(index ?? 0)}
            />
            <TypedCustomPagination
              total={paginationData?.total_items || 0}
              pageSize={paginationData?.page_size || pageSize}
              currentPage={paginationData?.current_page || currentPage}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
    </div>
  );
};

export default Wallet;
